import { useState } from 'react';
import CardNB from './ui-nb/CardNB';
import ButtonNB from './ui-nb/ButtonNB';
import { supabase } from '../lib/supabase';

interface DeleteOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderCode: string;
  onDeleted: () => void;
}

type ModalStatus = 'idle' | 'loading' | 'success' | 'error';

export default function DeleteOrderModal({
  isOpen,
  onClose,
  orderId,
  orderCode,
  onDeleted,
}: DeleteOrderModalProps) {
  const [confirmationInput, setConfirmationInput] = useState('');
  const [status, setStatus] = useState<ModalStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const isConfirmed = confirmationInput.toLowerCase() === orderCode.toLowerCase();

  const handleDelete = async () => {
    if (!isConfirmed || status === 'loading') return;

    setStatus('loading');
    setErrorMessage('');

    try {
      const { error: invokeError } = await supabase.functions.invoke('delete-order', {
        body: { order_id: orderId },
      });

      if (invokeError) throw invokeError;

      setStatus('success');
      setTimeout(() => {
        onDeleted();
        onClose();
        setConfirmationInput('');
        setStatus('idle');
      }, 1500);
    } catch (err) {
      setErrorMessage('Failed to delete order. Please try again.');
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  const handleClose = () => {
    if (status === 'loading') return;
    setConfirmationInput('');
    setStatus('idle');
    setErrorMessage('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      onClick={handleClose}
    >
      <CardNB
        className="max-w-sm w-full mx-4"
        style={{ border: '3px solid #000' }}
        onClick={(e) => e.stopPropagation()}
      >
        {status === 'success' ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <p style={{ color: '#000000', fontWeight: 600, fontSize: '1.125rem' }}>
              Order deleted successfully.
            </p>
          </div>
        ) : (
          <>
            <h2
              className="text-xl font-bold text-center mb-2"
              style={{ color: '#D64545' }}
            >
              Delete Order
            </h2>
            <p
              className="text-center mb-4"
              style={{ color: '#525252', fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              This action cannot be undone. All data for this order will be permanently deleted.
            </p>

            <div className="mb-4">
              <label
                htmlFor="delete-confirm"
                className="block mb-2"
                style={{ fontSize: '0.875rem', fontWeight: 500, color: '#000' }}
              >
                Type <span style={{ fontWeight: 700 }}>{orderCode}</span> to confirm
              </label>
              <input
                id="delete-confirm"
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                autoComplete="off"
                placeholder={orderCode}
                disabled={status === 'loading'}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '2px solid #000',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {errorMessage && (
              <p
                className="mb-4"
                style={{ color: '#D64545', fontSize: '0.875rem', textAlign: 'center' }}
              >
                {errorMessage}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <ButtonNB
                variant="danger"
                disabled={!isConfirmed || status === 'loading'}
                loading={status === 'loading'}
                onClick={handleDelete}
                style={{ flex: 1 }}
              >
                Delete Order
              </ButtonNB>
              <ButtonNB
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={status === 'loading'}
              >
                Cancel
              </ButtonNB>
            </div>
          </>
        )}
      </CardNB>
    </div>
  );
}
